import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  FormControl,
  FormLabel,
  Switch,
  Select,
  Option,
  Button,
  Chip,
  Divider,
} from '@mui/joy';
import { FilterList, Clear } from '@mui/icons-material';
import { User } from '@/types/user';

export interface PreferencesFilters {
  userId: number | null;
  preferenceCategory: 'all' | 'date' | 'day' | 'shift_type';
  preferenceType: 'all' | 'unavailable' | 'preferred' | 'avoid';
  showInactiveUsersOnly: boolean;
}

interface PreferencesFiltersProps {
  onFiltersChange: (filters: PreferencesFilters) => void;
  filters: PreferencesFilters;
  users?: User[];
  onClose?: () => void;
}

export default function PreferencesFilters({ onFiltersChange, filters, users = [], onClose }: PreferencesFiltersProps) {
  const [localFilters, setLocalFilters] = useState<PreferencesFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof PreferencesFilters, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const clearFilters = () => {
    const defaultFilters: PreferencesFilters = {
      userId: null,
      preferenceCategory: 'all',
      preferenceType: 'all',
      showInactiveUsersOnly: false,
    };
    setLocalFilters(defaultFilters);
    onFiltersChange(defaultFilters);
    onClose?.();
  };

  const hasActiveFilters = () => {
    return (
      localFilters.userId !== null ||
      localFilters.preferenceCategory !== 'all' ||
      localFilters.preferenceType !== 'all' ||
      localFilters.showInactiveUsersOnly
    );
  };

  // Filter users to only show active users for the user selection
  const activeUsers = users.filter(user => user.is_active);

  const applyFilters = () => {
    onFiltersChange(localFilters);
    onClose?.();
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterList />
            <Typography level="h4">Filters</Typography>
            {hasActiveFilters() && (
              <Chip size="sm" color="primary" variant="soft">
                Active
              </Chip>
            )}
          </Stack>

          <Stack spacing={2}>
            {/* User Filter */}
            <FormControl>
              <FormLabel>User</FormLabel>
              <Select
                placeholder="All users"
                value={localFilters.userId ? String(localFilters.userId) : ''}
                onChange={(_, value) => handleFilterChange('userId', value ? Number(value) : null)}
              >
                <Option value="">All Users</Option>
                {activeUsers.map((user) => (
                  <Option key={user.id} value={String(user.id)}>
                    {user.first_name} {user.last_name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {/* Preference Category Filter */}
            <FormControl>
              <FormLabel>Preference Category</FormLabel>
              <Select
                value={localFilters.preferenceCategory}
                onChange={(_, value) => handleFilterChange('preferenceCategory', value || 'all')}
              >
                <Option value="all">All Categories</Option>
                <Option value="date">Date Preferences</Option>
                <Option value="day">Day Preferences</Option>
                <Option value="shift_type">Shift Type Preferences</Option>
              </Select>
            </FormControl>

            <Divider />

            {/* Preference Type Filter */}
            <FormControl>
              <FormLabel>Preference Type</FormLabel>
              <Select
                value={localFilters.preferenceType}
                onChange={(_, value) => handleFilterChange('preferenceType', value || 'all')}
              >
                <Option value="all">All Types</Option>
                <Option value="unavailable">Unavailable</Option>
                <Option value="preferred">Preferred</Option>
                <Option value="avoid">Avoid</Option>
              </Select>
            </FormControl>

            <Divider />

            {/* Show Inactive Users */}
            <FormControl>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <FormLabel>Show Inactive Users</FormLabel>
                <Switch
                  checked={localFilters.showInactiveUsersOnly}
                  onChange={(e) => handleFilterChange('showInactiveUsersOnly', e.target.checked)}
                />
              </Stack>
            </FormControl>

          </Stack>

          <Divider />

          {/* Action Buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              startDecorator={<FilterList />}
              onClick={applyFilters}
              size="sm"
            >
              Apply Filters
            </Button>
            <Button
              startDecorator={<Clear />}
              onClick={clearFilters}
              size="sm"
              variant="outlined"
              color="neutral"
            >
              Clear All
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

